import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Instagram, Facebook, Twitter, MessageCircle, Copy, Download, RefreshCw, FileText } from 'lucide-react';
import { ShareableResultCard } from './ShareableResultCard';
import { Pattern } from '../../types/scan';
import { trackResultShared } from '../../utils/analytics';
type AspectRatio = 'square' | 'story';
interface SocialShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareAspectRatio: AspectRatio;
  onAspectRatioChange: (ratio: AspectRatio) => void;
  onSocialShare: (platform: 'instagram' | 'facebook' | 'tiktok' | 'twitter' | 'youtube' | 'whatsapp') => void;
  onDownloadImage: () => void;
  onDownloadPDF: () => void;
  isGeneratingImage: boolean;
  isGeneratingPDF: boolean;
  score: number;
  riskLevel: 'low' | 'medium' | 'high';
  messageCount: number;
  patterns: any[];
  summary?: string | null;
  shareCardTheme: 'dark' | 'light';
  onThemeChange: (theme: 'dark' | 'light') => void;
}
export function SocialShareModal({
  isOpen,
  onClose,
  shareAspectRatio,
  onAspectRatioChange,
  onSocialShare,
  onDownloadImage,
  onDownloadPDF,
  isGeneratingImage,
  isGeneratingPDF,
  score,
  riskLevel,
  messageCount,
  patterns,
  summary,
  shareCardTheme,
  onThemeChange
}: SocialShareModalProps) {
  if (!isOpen) return null;
  // Calculate scaling for the preview
  // The original cards are 1080px wide. We want to fit them in a ~300px container.
  const previewScale = 300 / 1080;
  const previewHeight = shareAspectRatio === 'story' ? 1920 * previewScale : 1080 * previewScale;
  return <AnimatePresence data-id="element-306" data-mp="22llrtvtrr9uf">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/95 backdrop-blur-md overflow-y-auto" data-id="element-307" data-mp="22hxyawobqnfp">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="w-full max-w-5xl bg-[#0A0A0A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row relative my-auto" data-id="element-308" data-mp="1tikog2wafwug">
          {/* Close Button */}
          <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-white/10 rounded-full text-white/70 hover:text-white transition-colors backdrop-blur-sm" data-id="element-309" data-mp="mzrtook6fi1l">
            <X size={20} data-id="element-310" data-mp="dybn6rwhg02n" />
          </button>

          {/* Left Side: Live Preview */}
          <div className="w-full md:w-1/2 bg-[#050505] border-r border-white/5 p-8 flex flex-col items-center justify-center relative overflow-hidden" data-id="element-311" data-mp="90jsxr8d81va">
            {/* Subtle background grid */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-312" data-mp="2tpppgmfiax9f" />

            <div className="relative z-10 flex flex-col items-center w-full" data-id="element-313" data-mp="1e68miu0oncbl">
              <h3 className="text-white/80 font-medium mb-6 uppercase tracking-widest text-sm" data-id="element-314" data-mp="1i8ddtm3nrwv2">
                Preview
              </h3>

              {/* Preview Container */}
              <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 transition-all duration-500 ease-in-out" style={{
              width: 300,
              height: previewHeight
            }} data-id="element-315" data-mp="1ryoi59r9882h">
                <div className="absolute top-0 left-0 origin-top-left transition-all duration-500 ease-in-out" style={{
                transform: `scale(${previewScale})`,
                width: 1080,
                height: shareAspectRatio === 'story' ? 1920 : 1080
              }} data-id="element-316" data-mp="25yhg9q541qkh">
                  <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={messageCount} topPatterns={patterns.slice(0, shareAspectRatio === 'story' ? 4 : 3)} variant={shareAspectRatio === 'story' ? 'vertical' : 'square'} summary={summary || undefined} theme={shareCardTheme} data-id="element-317" data-mp="3cijudfmpbstl" />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Controls */}
          <div className="w-full md:w-1/2 p-8 flex flex-col" data-id="element-318" data-mp="2ynzmnuo2urrc">
            <h2 className="text-2xl font-bold text-white mb-2" data-id="element-319" data-mp="2ayy9xv87oug9">
              Share Analysis
            </h2>
            <p className="text-white/50 text-sm mb-8" data-id="element-320" data-mp="25zllj61ozfbk">
              Export your forensic report or share it directly to social media.
            </p>

            {/* Format Toggle */}
            <div className="mb-8" data-id="element-321" data-mp="q9kqqp1nyha1">
              <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 block" data-id="element-322" data-mp="qam2ywuf19tc">
                Format
              </label>
              <div className="flex bg-white/5 p-1 rounded-xl border border-white/10" data-id="element-323" data-mp="gizwfdt0vywx">
                <button onClick={() => onAspectRatioChange('story')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareAspectRatio === 'story' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-324" data-mp="1492inda9aanm">
                  <div className="w-3 h-4 border-2 border-current rounded-[2px]" data-id="element-325" data-mp="105nufzc5ynz3" />
                  Story (9:16)
                </button>
                <button onClick={() => onAspectRatioChange('square')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareAspectRatio === 'square' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-326" data-mp="3kxg7vks35b84">
                  <div className="w-4 h-4 border-2 border-current rounded-[2px]" data-id="element-327" data-mp="1gplhgm65il0i" />
                  Square (1:1)
                </button>
              </div>
            </div>

            {/* Theme Toggle */}
            <div className="mb-8" data-id="element-328" data-mp="17onbhyc5k3fa">
              <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 block" data-id="element-329" data-mp="12rdmgjsodtrz">
                Card Style
              </label>
              <div className="flex bg-white/5 p-1 rounded-xl border border-white/10" data-id="element-330" data-mp="xuwoyyzl6328">
                <button onClick={() => onThemeChange('dark')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareCardTheme === 'dark' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-331" data-mp="tstfgfwh98jl">
                  <div className="w-4 h-4 rounded-full bg-[#0A0A0A] border border-white/20" data-id="element-332" data-mp="ew1ve7h85jhf" />
                  Dark
                </button>
                <button onClick={() => onThemeChange('light')} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${shareCardTheme === 'light' ? 'bg-white/10 text-white shadow-sm' : 'text-white/50 hover:text-white/80 hover:bg-white/5'}`} data-id="element-333" data-mp="jntnimg13x9o">
                  <div className="w-4 h-4 rounded-full bg-white border border-slate-200" data-id="element-334" data-mp="3w473ibieoog1" />
                  Light
                </button>
              </div>
            </div>

            {/* Social Grid */}
            <div className="mb-8" data-id="element-335" data-mp="1bf99qfdobhzd">
              <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 block" data-id="element-336" data-mp="1be7z2c0kkxi6">
                Share To
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-337" data-mp="s0h897ro3zaw">
                <ShareButton icon={<Instagram size={20} data-id="element-339" data-mp="n2p6zn54v0u1" />} label="Instagram" note="Copies caption" onClick={() => onSocialShare('instagram')} className="hover:bg-gradient-to-br hover:from-purple-600/20 hover:to-pink-600/20 hover:border-pink-500/50 hover:text-pink-400" data-id="element-338" data-mp="rymlrp0qzz4p" />
                <ShareButton icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" data-id="element-341" data-mp="15t6wzjykzibp">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" data-id="element-342" data-mp="1fqbkbn45xju3" />
                    </svg>} label="TikTok" note="Copies caption" onClick={() => onSocialShare('tiktok')} className="hover:bg-white/10 hover:border-white/30 hover:text-white" data-id="element-340" data-mp="1apnl4e5s5wos" />
                <ShareButton icon={<Facebook size={20} data-id="element-344" data-mp="32m9f1fatvc03" />} label="Facebook" onClick={() => onSocialShare('facebook')} className="hover:bg-blue-600/10 hover:border-blue-500/50 hover:text-blue-400" data-id="element-343" data-mp="32nb3qmkrnx0a" />
                <ShareButton icon={<Twitter size={20} data-id="element-346" data-mp="2epvkh1z0pqpv" />} label="X / Twitter" onClick={() => onSocialShare('twitter')} className="hover:bg-white/10 hover:border-white/30 hover:text-white" data-id="element-345" data-mp="2smqx14eha0ao" />
                <ShareButton icon={<MessageCircle size={20} data-id="element-348" data-mp="hx248l3138pv" />} label="WhatsApp" onClick={() => onSocialShare('whatsapp')} className="hover:bg-green-500/10 hover:border-green-500/50 hover:text-green-400" data-id="element-347" data-mp="cyimkwvgwj3b" />
                <ShareButton icon={<Copy size={20} data-id="element-350" data-mp="17dgs7use3ry3" />} label="Copy Link" onClick={async () => {
                try {
                  await navigator.clipboard.writeText(window.location.href);
                  // Fired only after the copy actually succeeded. The URL
                  // itself is never sent — it carries a scan id.
                  trackResultShared('copy_link', 'scan_result');
                } catch {
                  // Clipboard denied — nothing to report.
                }
                // Toast is handled in AnalysisPage
              }} className="hover:bg-white/10 hover:border-white/30 hover:text-white" data-id="element-349" data-mp="1l6g4lnwvl61f" />
              </div>
            </div>

            <div className="mt-auto space-y-3" data-id="element-351" data-mp="jp7ab3yaroc8">
              <button onClick={onDownloadImage} disabled={isGeneratingImage} className="w-full py-3.5 bg-white text-black rounded-xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-352" data-mp="b6cd1gylo507">
                {isGeneratingImage ? <>
                    <RefreshCw size={18} className="animate-spin" data-id="element-353" data-mp="3olido8sf49g5" />
                    Generating Image...
                  </> : <>
                    <Download size={18} data-id="element-354" data-mp="3jkudoxm7oi9s" />
                    Download Image
                  </>}
              </button>

              <button onClick={onDownloadPDF} disabled={isGeneratingPDF} className="w-full py-3.5 bg-transparent border border-white/20 text-white rounded-xl font-bold hover:bg-white/5 hover:border-white/40 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-355" data-mp="3tkla54uc17ci">
                {isGeneratingPDF ? <>
                    <RefreshCw size={18} className="animate-spin" data-id="element-356" data-mp="19qb5ziwkwewe" />
                    Generating PDF...
                  </> : <>
                    <FileText size={18} data-id="element-357" data-mp="1ijegbibxgbmt" />
                    Download PDF Case File
                  </>}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}
function ShareButton({
  icon,
  label,
  note,
  onClick,
  className
}: {
  icon: React.ReactNode;
  label: string;
  note?: string;
  onClick: () => void;
  className: string;
}) {
  return <button onClick={onClick} className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border border-white/10 bg-white/5 transition-all text-white/70 ${className}`} data-id="element-358" data-mp="zv9gglye5ata">
      <div className="mb-1" data-id="element-359" data-mp="2b7aaoge0xrgf">{icon}</div>
      <span className="text-xs font-bold" data-id="element-360" data-mp="2lcsay4uet7em">{label}</span>
      {note && <span className="text-[9px] text-white/40 font-medium" data-id="element-361" data-mp="2lewe2kkhi2du">{note}</span>}
    </button>;
}